/**
 * Framework-free display + search helpers for message participants, shared by the
 * client and town-square DM surfaces (previously duplicated in each app's
 * participantHelpers). Proven in shared/test/message-participant.spec.ts.
 */

export interface ParticipantUser {
  asfId?: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  // No email: User.email is owner/admin-only on the API, so a thread partner's
  // email always resolves to null. The public display fallback is the profile.
  profile?: { displayName?: string | null } | null;
}

export interface ParticipantContact {
  id?: string;
  name?: string | null;
  email?: string;
}

export interface MessageParticipant {
  id?: string;
  userId?: string | null;
  contactId?: string | null;
  user?: ParticipantUser | null;
  contact?: ParticipantContact | null;
}

export interface MessageSender {
  kind?: string;
  userId?: string | null;
  contactId?: string | null;
  user?: ParticipantUser | null;
  contact?: ParticipantContact | null;
}

/**
 * Display name for a message participant. Falls back to the @handle before
 * "Unknown User" so username-only accounts don't render as "Unknown User".
 */
export function getParticipantDisplayName(participant: MessageParticipant): string {
  if (participant.user) {
    const firstName = participant.user.firstName || '';
    const lastName = participant.user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || participant.user.profile?.displayName || participant.user.username || 'Unknown User';
  }

  if (participant.contact) {
    return participant.contact.name || participant.contact.email || 'Unknown Contact';
  }

  return 'Deleted User';
}

/**
 * Inbox and thread header title. A guild room is the guild; otherwise the other
 * participant, or a translated group label when there are several.
 */
export function threadTitle(
  guildName: string | null | undefined,
  others: MessageParticipant[],
  labels: { unknown: string; group: (first: string, extra: number) => string }
): string {
  if (guildName != null) return guildName;
  const first = others[0];
  if (!first) return labels.unknown;
  const name = getParticipantDisplayName(first);
  if (others.length === 1) return name;
  return labels.group(name, others.length - 1);
}

/** Display name for a message sender (same rules as {@link getParticipantDisplayName}). */
export function getSenderDisplayName(sender: MessageSender): string {
  if (sender.user) {
    const firstName = sender.user.firstName || '';
    const lastName = sender.user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || sender.user.profile?.displayName || sender.user.username || 'Unknown User';
  }

  if (sender.contact) {
    return sender.contact.name || sender.contact.email || 'Unknown Contact';
  }

  return 'Deleted User';
}

/**
 * Lowercased, space-joined bag of every field a thread can be searched by: real
 * name, profile display name, username handle, and (for contact-form threads) the
 * contact's name/email. The DM list-search predicate matches against this so
 * typing a username like "lauretta_" finds the thread even with no name set.
 */
export function getParticipantSearchText(participant: MessageParticipant): string {
  const parts: Array<string | null | undefined> = [];
  if (participant.user) {
    parts.push(
      participant.user.firstName,
      participant.user.lastName,
      participant.user.profile?.displayName,
      participant.user.username
    );
  }
  if (participant.contact) {
    parts.push(participant.contact.name, participant.contact.email);
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}
